/* eslint-disable max-len */
import path from 'node:path';
import os from 'node:os';
import { fileURLToPath } from 'node:url';

import merge from 'lodash.merge';

// Required backend utilities
import { readJSONWithMemo } from '#backend/util/readJSON.js';
import getThemes from '#backend/util/getThemes.js';

// Array of countries used in state selectors
import stateListBR from './brazilStateList/brazilStateList-unformatted.json' with { type: 'json' };

// Array of countries used in a country selector
import countryList from './countryList/countryList-unformatted.json' with { type: 'json' };

// Set global variables for server process
if (typeof global === 'object') {
	global.IS_CLIENT = false;
	global.IS_SERVER = true;
}

// Set env object and export it
const env = {};

// Set environment variables
env.environment = process.env.NODE_ENV || 'local';
env.isUnitTest = env.environment === 'unit-test';
env.isBuildProcess = env.environment === 'build';
env.isDev = env.environment === 'dev';
env.isStage = env.environment === 'stage';
env.isProduction = env.environment === 'prod';
env.isLocalhost = !['dev', 'stage', 'prod'].includes(env.environment);

// Set providers environment (server side)
if (IS_SERVER) {
	env.providersEnvironment = env.environment;
}

// Set providers environment (client side)
if (IS_CLIENT) {
	const [, hostnameEnvironment] = location.hostname.match(/checkout-(local|dev|stage)/) || [null, 'prod'];
	env.providersEnvironment = hostnameEnvironment;
}

if (IS_SERVER) {
	// ZIP codes map used for ZIP to country+state validation
	const { default: zipMapUS } = !env.isUnitTest ? await import('#backend/config/zipMap/us-zip-unformatted.json', {
		with: {
			type: 'json'
		}
	}) : await import('#backend/config/zipMap/test/us-zip-formatted-test.json', {
		with: {
			type: 'json'
		}
	});
	const { default: zipMapCA } = !env.isUnitTest ? await import('#backend/config/zipMap/ca-zip-unformatted.json', {
		with: {
			type: 'json'
		}
	}) : await import('#backend/config/zipMap/test/ca-zip-formatted-test.json', {
		with: {
			type: 'json'
		}
	});
	const { default: zipMapIN } = !env.isUnitTest ? await import('#backend/config/zipMap/in-zip-unformatted.json', {
		with: {
			type: 'json'
		}
	}) : await import('#backend/config/zipMap/test/in-zip-formatted-test.json', {
		with: {
			type: 'json'
		}
	});
	env.zipMap = {
		us: {
			country: 'US',
			characters: 5,
			default: {
				city: 'California',
				state: 'CA'
			},
			zipCodes: zipMapUS
		},
		ca: {
			country: 'CA',
			characters: 6,
			default: {
				city: 'Alberta',
				state: 'AB'
			},
			zipCodes: zipMapCA
		},
		in: {
			country: 'IN',
			characters: 6,
			default: {
				city: 'Chennai',
				state: 'TN'
			},
			zipCodes: zipMapIN
		},
		br: {
			country: 'BR',
			characters: 8,
			states: stateListBR
		}
	};

	const projectPath = path.join(path.dirname(fileURLToPath(import.meta.url)), '../..');
	const backendPath = path.join(projectPath, 'backend');
	const frontendPath = path.join(projectPath, 'frontend');
	const sharedPath = path.join(projectPath, 'shared');
	const themesPath = path.join(sharedPath, 'themes');

	// Store current datacenter information
	env.datacenter = process.env.DATACENTER || 'localhost';

	// Define known datacenters
	env.datacenters = ['europe-west1', 'europe-west3', 'us-east1', 'us-west1'];

	// Set paths
	env.paths = {
		projectPath,
		backendPath,
		frontendPath,
		sharedPath,
		themesPath
	};

	// Load package.json for debug purposes
	env.packageJson = readJSONWithMemo(path.join(env.paths.projectPath, '../package.json'));

	// Add application version
	env.appVersion = env.packageJson.buildVersion || env.packageJson.version;
	env.appVersionSlug = env.appVersion.replace(/\./g, '-');

	// Set ports
	env.port = Number(process.env.PORT || 443);

	// Enable cluster module (limit maximum of workers by value of cpus threads)
	const availableThreads = os.cpus().length;
	env.nodeClusterEnabled = process.env.CHECKOUT_CLUSTER_ENABLED === 'true';
	env.nodeClusterWorkers = Number(process.env.CHECKOUT_CLUSTER_WORKERS || availableThreads);
	env.nodeClusterWorkers = env.nodeClusterWorkers > availableThreads ? availableThreads : env.nodeClusterWorkers;

	// Load themes
	env.themes = await getThemes(themesPath, [
		'components/global/global.js',
		'components/error/error.js',
		'localization',
		'localization/localization.json',
		'app.js',
		'config.js',
		'routes.js',
		'template.html'
	]);

	// Add providers ENV variables
	env.providers = {
		checkout: {
			credentials: {
				apiKey: process.env.CHECKOUT_API_KEY
			}
		},
		ipm: {
			credentials: {
				accountIdTimestampSecret: process.env.ACCOUNTIDTIMESTAMP_SECRET
			}
		},
		pp: {
			credentials: {
				apiSecret: process.env.PP_API_SECRET,
				timestampSecret: process.env.PP_TIMESTAMP_SECRET
			}
		}
	};
}

// Necessary query parameters (others can be removed in case the url is too long)
env.necessaryParameters = ['product', 'quantity', 'customID', 'aid', 'campaign', 'aswparam', 'afbData', 'clearCart', 'provider', 'agentDesignation', 'rtprice'];

/*
 * Whitelist for query parameters and cookie names
 * -----
 * additional2 - for PP to know if the order was up-sell (Avast specific)
 * afbData - encoded set of parameters, base64, escaped (SMB)
 * agentDesignation - allow tracking of links generated by agents
 * aid (aka. accountIdTimestamp) - NLOK accountGuidTimestamp cipher used to apply authenticated checkout on web
 * aswparam - encoded set of parameters, base64, escaped
 * avgRenewalFlag - for PP to know if the order was up-sell (AVG specific)
 * campaign - PP campaign ID
 * campaignMarker - parameter for tracking the performance of marketing campaigns
 * cid - for pairing tracking across platforms
 * clearCart - removes all products from the cart
 * customID - parameter(s) for customization, comma separated
 * debugBar (aka. isDebug) - displays debugBar (developer mode required)
 * env - for setting provider environments on localhost
 * h - (aka. linkHash) hash of some query parameters combination we will validate and return discount or not (https://butr.avast.com/browse/CART-4753)
 * hotjar - Hotjar tracking parameter
 * myAppContext - analytics parameter including app details used by MyAccount to know where the call originated, base64
 * orderSource - enables to rewrite source in XML, used also to differentiate AB tests from regular traffic
 * paymentInfo - encoded set of sca parameters from provider, base64, escaped
 * paymentStatus - status of the sca payment (either SUCCESS or FAILURE)
 * product - ID(s) of the product(s), comma separated
 * provider (aka. providerName) - name of eCom provider to process the order
 * quantity - quantity(ies) of product(s), comma separated
 * rtprice - encoded set of parameters required for IPM/OPM retargeting toaster
 * shopper (aka. providerAccountId) - nlokGuid to pair with MyAccount account
 * t - (aka. linkTimestamp) timestamp of when the link was generated from Pricing (https://butr.avast.com/browse/CART-4753)
 * testOrder (aka. isTest) - enables finish an order (developer mode required)
 * trackingDisabled - for disabling GTM
 * utm_* - GA tracking parameter
 * trToken (aka. transferToken) - parameter used for passing cookies between domains (e.g. affiliate)
 */
env.whitelist = [...env.necessaryParameters, 'campaignMarker', 'cid', 'developer', 'avgRenewalFlag', 'additional2', 'utm_*', 'orderSource', 'myAppContext', 'trackingDisabled', 'hotjar', 'paymentInfo', 'paymentStatus', 'env', 't', 'h', 'debugBar', 'testOrder', 'trToken'];

/**
 * Blacklist for query parameters
 * @note On client, these parameters are removed from retargetingUrl
 * @note On server, these parameters won't be returned anywhere in the server response
 */
env.blacklist = IS_CLIENT ? ['shopper', 'aid'] : ['shopper', 'aid', 'aswparam', 'afbData', 'cid', 'myAppContext', 'trToken'];

// Query parameters to be removed from url once the cart is loaded
env.toClearParameters = ['clearCart', 'provider', 'currency', 't', 'h', 'paymentInfo', 'paymentStatus', 'developer', 'env', 'debugBar', 'testOrder'];

// Payment methods which will turn auto-renewal purchase into manual-renewal one
env.offlineMethods = {
	gen: ['wireTransfer', 'boleto']
};

// Payment methods which does not support recurring payments
env.manualRenewalMethods = {
	gen: ['wireTransfer', 'boleto']
};

// Payment methods which are allowed to be used for purchase of trial products
env.trialMethods = {
	gen: ['creditCard', 'payPalBilling', 'bancontact']
};

// Payment methods which will allow using the company flow
env.companyMethods = {
	gen: ['creditCard', 'wireTransfer', 'iDeal', 'bancontact', 'sofort', 'directDebit']
};

// Payment methods which will have disabled country selector
env.fixedCountryMethods = {
	gen: ['wireTransfer', 'directDebit', 'iDeal', 'sofort', 'bancontact']
};

// Payment methods which will use redirect flows
env.redirectMethods = {
	gen: ['iDeal', 'sofort'] // Redirect, then submit
};

// Payment methods which does not require provider frontend library to submit the order
env.librarySkippableMethods = {
	gen: ['iDeal', 'sofort', 'wireTransfer', 'boleto']
};

// Payment methods which support installments
env.installmentsMethods = {
	gen: ['creditCard']
};

// Payment methods for OMS flow
env.omsMethods = {
	gen: ['creditCard']
};

// Special zip codes validations
env.zipMapCountries = ['US', 'CA', 'IN'];
env.customZipValidationCountries = [...env.zipMapCountries, 'BR', 'JP'];

// Array of countries with VAT input field enabled
env.vatCountries = {
	gen: ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'TW', 'XI'] // GEN supports additional: 'IN', 'NO', 'NZ', 'GB'
};

// Array of countries with flag
env.countriesWithFlag = ['AR', 'AU', 'AT', 'BE', 'BR', 'CA', 'CL', 'CN', 'CO', 'CR', 'HR', 'CZ', 'DK', 'FI', 'FR', 'DE', 'HK', 'HU', 'IL', 'IT', 'JP', 'KZ', 'LU', 'MY', 'MX', 'NL', 'NZ', 'NO', 'PE', 'PL', 'PT', 'QA', 'RU', 'SA', 'SG', 'SK', 'ZA', 'KR', 'ES', 'SE', 'CH', 'TW', 'TR', 'GB', 'US', 'UY'];

// Array of all supported WT countries and their currencies
env.wireTransferCountries = [
	{
		value: 'JP',
		currency: 'JPY'
	},
	{
		value: 'AT',
		currency: 'EUR'
	},
	{
		value: 'CZ',
		currency: 'CZK'
	},
	{
		value: 'EE',
		currency: 'EUR'
	},
	{
		value: 'FI',
		currency: 'EUR'
	},
	{
		value: 'DE',
		currency: 'EUR'
	},
	{
		value: 'GR',
		currency: 'EUR'
	},
	{
		value: 'IE',
		currency: 'EUR'
	},
	{
		value: 'IT',
		currency: 'EUR'
	},
	{
		value: 'LU',
		currency: 'EUR'
	},
	{
		value: 'NL',
		currency: 'EUR'
	},
	{
		value: 'NO',
		currency: 'NOK'
	},
	{
		value: 'PL',
		currency: 'PLN'
	},
	{
		value: 'PT',
		currency: 'EUR'
	},
	{
		value: 'SK',
		currency: 'EUR'
	},
	{
		value: 'ES',
		currency: 'EUR'
	},
	{
		value: 'CH',
		currency: 'CHF'
	},
	{
		value: 'GB',
		currency: 'GBP'
	},
	{
		value: 'CA',
		currency: 'CAD'
	},
	{
		value: 'US',
		currency: 'USD'
	},
	{
		value: 'AU',
		currency: 'AUD'
	},
	{
		value: 'NZ',
		currency: 'NZD'
	}
];

env.countries = countryList.map((country) => ({
	...country,
	hasIcon: env.countriesWithFlag.includes(country.value)
}));

// Domains excluded from the email domain check
env.domainExceptions = ['gmx.net'];

// Gen merchantID map
env.merchantMap = {
	symantec: ['AS', 'AI', 'AG', 'AR', 'AW', 'BS', 'BB', 'BZ', 'BM', 'BO', 'BQ', 'BR', 'CA', 'KY', 'CL', 'CO', 'CR', 'CU', 'CW', 'DM', 'DO', 'EC', 'SV', 'FK', 'GF', 'GD', 'GP', 'GU', 'GT', 'GY', 'HT', 'HN', 'JM', 'MQ', 'MX', 'MS', 'AN', 'NI', 'MP', 'PA', 'PY', 'PE', 'PR', 'KN', 'LC', 'PM', 'VC', 'SX', 'GS', 'SR', 'TT', 'TC', 'US', 'UM', 'UY', 'VE', 'VG', 'VI'],
	symantec_pacrim: ['AU', 'BD', 'BT', 'BN', 'KH', 'CN', 'CX', 'CC', 'CK', 'TL', 'FJ', 'HM', 'HK', 'IN', 'ID', 'JP', 'KI', 'LA', 'MO', 'MY', 'MV', 'MH', 'FM', 'MN', 'MM', 'NR', 'NP', 'NZ', 'NU', 'NF', 'KP', 'PW', 'PG', 'PH', 'WS', 'SG', 'SB', 'KR', 'LK', 'TW', 'TH', 'TK', 'TO', 'TV', 'VU', 'VN']
};

// Gen merchantAddress map for invoice seller info
env.merchantAddressMap = {
	symantec_uk: ['BE', 'GB', 'IE', 'LU', 'NL'],
	symantec_jp: ['JP']
};

if (!env.isBuildProcess && !env.isUnitTest) {
	// Environment configurations
	const { default: envLocal } = await import('./env/local.js');
	const { default: envDev } = await import('./env/dev.js');
	const { default: envStage } = await import('./env/stage.js');
	const { default: envProd } = await import('./env/prod.js');
	env.environmentConfigurations = {
		local: envLocal,
		dev: envDev,
		stage: envStage,
		prod: envProd
	};
	// Merge with environment config (highest priority)
	merge(env, env.environmentConfigurations[env.providersEnvironment]);
}

export default env;
